/**
 * WaitlistCard — three-state UI shown beneath a no-slots-available
 * empty state. Customer can opt in to be notified when a slot frees
 * up on the date they wanted.
 *
 * State machine:
 *   1. submitted? → green confirmation card.
 *   2. form open? → notes textarea + "Join Waitlist" button.
 *   3. otherwise → underlined "Or join the waitlist for this date" link.
 *
 * Calls POST /api/waitlist with { name, email, phone?, appointmentType?,
 * preferredDate?, notes? }. The four contact fields come from the parent
 * via props (typically same state ContactStep is bound to).
 *
 * Extracted from consultation/page.tsx — Phase 1.3 of booking
 * consolidation (wiki/plans/booking-consolidation-2026-04-28.md).
 * Behavior preserved verbatim: same toast keys, same trackEvent name,
 * same fallback English copy on the marketing strings.
 */
"use client";

import { useState } from "react";
import { Loader2 } from "lucide-react";
import { useTranslations } from "next-intl";
import { useToast } from "@/context/ToastContext";
import { trackEvent } from "@/lib/track";

interface WaitlistCardProps {
  /** Required for /api/waitlist body. */
  name: string;
  /** Required for /api/waitlist body. */
  email: string;
  /** Optional — sent as null when blank. */
  phone: string;
  /** ISO date "YYYY-MM-DD" the customer was browsing. */
  date: string;
  /** Service or consultation type the customer wanted. */
  appointmentType: string;
  /**
   * i18n namespace. Defaults to "consultation" so existing translation
   * files keep working. Phase 2 will rename to a shared "booking"
   * namespace.
   */
  i18nNamespace?: string;
}

export default function WaitlistCard({
  name,
  email,
  phone,
  date,
  appointmentType,
  i18nNamespace = "consultation",
}: WaitlistCardProps) {
  const t = useTranslations(i18nNamespace);
  const { toast } = useToast();
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [notes, setNotes] = useState("");

  const handleJoin = async () => {
    if (!name || !email) {
      toast(t("fillNameEmailFirst"), "error");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch("/api/waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          email,
          phone: phone || null,
          appointmentType: appointmentType || null,
          preferredDate: date || null,
          notes: notes || null,
        }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Failed to join waitlist");
      }
      setSubmitted(true);
      trackEvent("waitlist_joined", { date, type: appointmentType });
      toast(t("addedToWaitlist"), "success");
    } catch (err) {
      toast(
        err instanceof Error ? err.message : "Failed to join waitlist",
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 w-full max-w-sm">
        <p className="text-sm font-medium text-emerald-700">
          You&apos;re on the waitlist!
        </p>
        <p className="text-xs text-emerald-600 mt-1 leading-relaxed">
          No payment now. If a slot opens we&apos;ll text + email a booking
          link &mdash; first come, first served (30 min to claim).
        </p>
      </div>
    );
  }

  if (!showForm) {
    return (
      <button
        onClick={() => setShowForm(true)}
        className="text-sm font-medium text-brand-gold hover:text-brand-gold-dark transition-colors underline underline-offset-2"
      >
        Or join the waitlist for this date
      </button>
    );
  }

  return (
    <div className="w-full max-w-sm space-y-3 text-start">
      <p className="text-xs font-medium text-brand-dark">
        Join the waitlist and we&apos;ll notify you when a slot opens.
      </p>
      <p className="text-[11px] text-brand-muted leading-relaxed">
        No payment now &mdash; if a slot opens we&apos;ll text and email you a
        booking link. You&apos;ll have 30 minutes to claim it before it
        passes to the next person on the list.
      </p>
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Any notes or preferences? (optional)"
        rows={2}
        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-brand-dark focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-transparent"
      />
      <button
        onClick={handleJoin}
        disabled={submitting}
        className="w-full rounded-lg bg-brand-gold px-4 py-2.5 text-sm font-medium text-white hover:bg-brand-gold-dark transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
      >
        {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
        Join Waitlist
      </button>
    </div>
  );
}
